import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  IconButton,
  Chip,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Divider,
  InputAdornment,
  Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import useStockMovements from '../../hooks/useStockMovements';
import LoadingSpinner from '../../components/LoadingSpinner';
import SearchIcon from '@mui/icons-material/Search';

/**
 * Admin Stock Movements Page
 * Displays a table of all stock movements with filtering and pagination
 * @returns {JSX.Element} Stock Movements Page
 */
const StockMovementsPage = () => {
  const { t } = useTranslation('misc');
  useDocumentTitle(t('stockMovements.title'));

  const [searchQuery, setSearchQuery] = useState('');
  const [returnIds, setReturnIds] = useState({});

  const {
    loading,
    movements,
    page,
    rowsPerPage,
    totalMovements,
    reasonFilter,
    stats,
    handleChangePage,
    handleChangeRowsPerPage,
    handleSearch,
    handleReasonFilter,
    refreshData,
    getReasonText,
  } = useStockMovements();

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Fetch parent return IDs for return items
  useEffect(() => {
    async function fetchReturnData() {
      if (!movements || movements.length === 0) return;

      const newReturnIds = {};

      for (const movement of movements) {
        if (movement.returnItemId && movement.returnItem) {
          // First check if the returnItem has a return property with an id
          if (movement.returnItem.return && movement.returnItem.return.id) {
            newReturnIds[movement.returnItemId] = movement.returnItem.return.id;
          }
          // Then check if it has a returnId property
          else if (movement.returnItem.returnId) {
            newReturnIds[movement.returnItemId] = movement.returnItem.returnId;
          }
          // Fallback to using the returnItemId itself
          else {
            newReturnIds[movement.returnItemId] = movement.returnItemId;
          }
        }
      }

      setReturnIds(newReturnIds);
    }

    fetchReturnData();
  }, [movements]);

  return (
    <Box sx={{ padding: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography
          variant="h4"
          gutterBottom
        >
          {t('stockMovements.title')}
        </Typography>
        <Box>
          <Tooltip title={t('stockMovements.refresh')}>
            <IconButton
              onClick={refreshData}
              color="primary"
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid
        container
        spacing={3}
        sx={{ mb: 3 }}
      >
        <Grid
          item
          xs={12}
          md={4}
        >
          <Card
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <CardContent
              sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="h6"
                color="text.secondary"
                gutterBottom
              >
                {t('stockMovements.totalMovements')}
              </Typography>
              <Typography variant="h4">{stats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <Card
            sx={{
              bgcolor: '#e3f2fd',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <CardContent
              sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="h6"
                color="text.secondary"
                gutterBottom
              >
                {t('stockMovements.stockIncreases')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUpIcon sx={{ color: 'success.main', mr: 1 }} />
                <Typography variant="h4">{stats.increases}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <Card
            sx={{
              bgcolor: '#ffebee',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <CardContent
              sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="h6"
                color="text.secondary"
                gutterBottom
              >
                {t('stockMovements.stockDecreases')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingDownIcon sx={{ color: 'error.main', mr: 1 }} />
                <Typography variant="h4">{stats.decreases}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography
          variant="h6"
          gutterBottom
        >
          {t('stockMovements.filters')}
        </Typography>
        <Grid
          container
          spacing={2}
          alignItems="flex-end"
        >
          <Grid
            item
            xs={12}
            md={6}
          >
            <TextField
              variant="outlined"
              fullWidth
              placeholder={t('stockMovements.searchByOrderId')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
          >
            <FormControl fullWidth>
              <InputLabel id="reason-filter-label">{t('stockMovements.reason')}</InputLabel>
              <Select
                labelId="reason-filter-label"
                id="reason-filter"
                value={reasonFilter}
                label={t('stockMovements.reason')}
                onChange={(e) => handleReasonFilter(e.target.value)}
              >
                <MenuItem value="">{t('stockMovements.all')}</MenuItem>
                <MenuItem value="order_placement">{t('stockMovements.orderPlacement')}</MenuItem>
                <MenuItem value="order_cancellation">
                  {t('stockMovements.orderCancellation')}
                </MenuItem>
                <MenuItem value="return_approved">{t('stockMovements.returnApproved')}</MenuItem>
                <MenuItem value="return_restocked">{t('stockMovements.returnRestocked')}</MenuItem>
                <MenuItem value="manual_update">{t('stockMovements.manualUpdate')}</MenuItem>
                <MenuItem value="initial_stock">{t('stockMovements.initialStock')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Stock Movements Table */}
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <LoadingSpinner />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('stockMovements.id')}</TableCell>
                    <TableCell>{t('stockMovements.date')}</TableCell>
                    <TableCell>{t('stockMovements.product')}</TableCell>
                    <TableCell align="right">{t('stockMovements.quantityChange')}</TableCell>
                    <TableCell align="right">{t('stockMovements.newStockLevel')}</TableCell>
                    <TableCell>{t('stockMovements.reason')}</TableCell>
                    <TableCell>{t('stockMovements.relatedTo')}</TableCell>
                    <TableCell>{t('stockMovements.notes')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {movements.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        align="center"
                      >
                        {t('stockMovements.noStockMovementsFound')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    movements.map((movement) => (
                      <TableRow key={movement.id}>
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontFamily="monospace"
                            sx={{ fontSize: '0.75rem' }}
                          >
                            {movement.id.substring(0, 8)}...
                          </Typography>
                          {movement.orderId && (
                            <Typography
                              variant="caption"
                              color="primary"
                              sx={{ display: 'block', fontSize: '0.7rem' }}
                            >
                              Order: {movement.orderId.substring(0, 8)}...
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{formatDate(movement.createdAt)}</TableCell>
                        <TableCell>
                          <Box>
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                            >
                              {movement.product?.name || t('stockMovements.unknownProduct')}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {movement.product?.sku || t('stockMovements.noSku')}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={
                              movement.quantityChange > 0
                                ? `+${movement.quantityChange}`
                                : movement.quantityChange
                            }
                            color={movement.quantityChange > 0 ? 'success' : 'error'}
                            sx={{
                              fontWeight: 'bold',
                              minWidth: '60px',
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">{movement.newStockLevel}</TableCell>
                        <TableCell>
                          <Chip
                            label={getReasonText(movement.reason)}
                            size="small"
                            color={
                              movement.reason === 'return_restocked' ||
                                movement.reason === 'initial_stock' ||
                                movement.reason === 'order_cancellation'
                                ? 'success'
                                : movement.reason === 'order_placement'
                                  ? 'error'
                                  : 'secondary'
                            }
                            variant={movement.reason === 'manual_update' ? 'outlined' : 'filled'}
                          />
                        </TableCell>
                        <TableCell>
                          {movement.orderId && (
                            <Typography
                              variant="body2"
                              sx={{ mb: 0.5 }}
                            >
                              {t('stockMovements.order')}:{' '}
                              <RouterLink
                                to={`/admin/orders/${movement.orderId}`}
                                className="router-link"
                              >
                                {movement.order?.orderNumber ||
                                  `${movement.orderId.substring(0, 8)}...`}
                              </RouterLink>
                            </Typography>
                          )}
                          {movement.returnItemId && (
                            <Typography
                              variant="body2"
                              sx={{ mb: 0.5 }}
                            >
                              {t('stockMovements.return')}:{' '}
                              <RouterLink
                                to={`/admin/returns/${returnIds[movement.returnItemId] || movement.returnItemId
                                  }`}
                                className="router-link"
                              >
                                {`${movement.returnItemId.substring(0, 8)}...`}
                              </RouterLink>
                            </Typography>
                          )}
                          {movement.adminUserId && (
                            <Typography variant="body2">
                              {t('stockMovements.admin')}:{' '}
                              {movement.admin?.name || `${movement.adminUserId.slice(0, 8)}...`}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {movement.notes && movement.notes.includes('order') && movement.orderId
                            ? movement.notes.replace(
                              new RegExp(`order ${movement.orderId}`, 'i'),
                              `order ${movement.order?.orderNumber || movement.orderId.substring(0, 12)
                              }`,
                            )
                            : movement.notes || '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Divider />
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalMovements}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default StockMovementsPage;
